import { IFilterType, IColumnTypes, IColumnValuesCount } from 'components/spreadsheet/types'
import {
  ITableViewFilter,
  ITableRow,
  ITableViewColumn,
  IFile,
  IVote,
  ITag,
  ICellValue,
  ICommentThreadStats
} from 'types'
import {
  DATE_COLUMN_TYPES,
  TEXT_COLUMN_TYPES,
  FilterVariables,
  NUMBER_COLUMN_TYPES,
  ATTACHMENT_COLUMN_TYPES,
  ValidationDisplayTypes,
  BOOLEAN_COLUMN_TYPES,
  VOTE_COLUMN_TYPES,
  CommentDisplayTypes
} from 'components/spreadsheet/constants/const'
import { isSummaryUser, getRowValue } from 'components/spreadsheet/helpers/functions'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { recordListsEqual } from 'components/spreadsheet/helpers/record-equality'

// Value-based comparison of two lists of filter rules, insensitive to object key
// order and to whether the optional value / multipleValues fields are present.
// The filter menu rebuilds rule objects (different key order, `publicId` dropped)
// and the saved baseline is run through convertToApiFilter, so comparing with
// JSON.stringify flagged phantom "unsaved changes" after a net-zero filter edit.
// recordListsEqual compares the union of keys, so a new attribute added to
// ITableViewFilter is picked up automatically without editing this helper.
export const areFilterSettingsEqual = (a: ITableViewFilter[], b: ITableViewFilter[]): boolean => {
  return recordListsEqual(a as unknown as Record<string, unknown>[], b as unknown as Record<string, unknown>[])
}

// Helper to convert array-based rowData to object format for isValidValue
const rowDataToObject = (
  row: ITableRow,
  columns: ITableViewColumn[],
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
): { [columnId: string]: ICellValue } => {
  const result: { [columnId: string]: ICellValue } = {}
  for (const column of columns) {
    const index = columnIdToIndex.get(column.publicId)
    if (index !== undefined) {
      result[column.publicId] = row.rowData?.[index] ?? (columnStore ? columnStore.get(row._rowIndex, index) : null)
    }
  }
  return result
}

// Filters grouped by OR group, each paired with its column — resolved once per pass rather than
// scanned for on every row.
type PreparedFilterGroups = Record<string, { filter: ITableViewFilter; column: ITableViewColumn }[]>

const prepareFilterGroups = (filters: ITableViewFilter[], columns: ITableViewColumn[]): PreparedFilterGroups => {
  const columnsById = new Map(columns.map((column) => [column.publicId, column]))
  const groups: PreparedFilterGroups = {}
  for (const filter of filters) {
    // A group keeps its entry even when every filter in it is dropped: a group with nothing left
    // to check matches every row, as it always has.
    if (!groups[filter.orGroup]) groups[filter.orGroup] = []
    const column = columnsById.get(filter.columnId)
    // Filters on columns not present in the view (e.g. hidden columns) are applied server-side.
    if (!column) continue
    groups[filter.orGroup].push({ filter, column })
  }
  return groups
}

// Comment stats by row id, for the comment row filters. Only built when one of them is on.
const commentStatsByRow = (
  comments: ICommentThreadStats[] | undefined,
  displayCommentRows: number
): Map<string, ICommentThreadStats> | null => {
  if (!comments || displayCommentRows === CommentDisplayTypes.SHOW_ALL) return null
  return new Map(comments.map((comment) => [comment.referenceId, comment]))
}

// Returns a visibility bitmap over all rows — no array copies.
export const filterRows = (
  rows: ITableRow[],
  columnValuesCount: IColumnValuesCount,
  filters: ITableViewFilter[],
  userEmail: string,
  userId: string,
  userTags: ITag[],
  processTags: ITag[],
  processVariables: string[],
  processVariableValues: string[],
  columns: ITableViewColumn[],
  displayValidationErrorRows: number,
  displayCommentRows: number,
  columnIdToIndex: Map<string, number>,
  searchedRows: Map<string, string[]>,
  comments?: ICommentThreadStats[],
  filterSearchRows?: boolean,
  columnStore?: CompressedColumnStore | null
): { bitmap: Uint8Array; visibleCount: number } => {
  const bitmap = new Uint8Array(rows.length)
  let visibleCount = 0

  const filterGroups = prepareFilterGroups(filters, columns)
  const commentStats = commentStatsByRow(comments, displayCommentRows)

  for (let i = 0; i < rows.length; i++) {
    const visible = isRowVisible(
      rows[i],
      columnValuesCount,
      filterGroups,
      commentStats,
      userEmail,
      userId,
      userTags,
      processTags,
      processVariables,
      processVariableValues,
      columns,
      displayValidationErrorRows,
      displayCommentRows,
      columnIdToIndex,
      searchedRows,
      filterSearchRows,
      columnStore
    )

    if (visible) {
      bitmap[i] = 1
      visibleCount++
    }
  }

  return { bitmap, visibleCount }
}

// Returns true if row matchs a filter
export const isRowVisible = (
  row: ITableRow,
  columnValuesCount: IColumnValuesCount,
  filterGroups: PreparedFilterGroups,
  commentStats: Map<string, ICommentThreadStats> | null,
  userEmail: string,
  userId: string,
  userTags: ITag[],
  processTags: ITag[],
  processVariables: string[],
  processVariableValues: string[],
  columns: ITableViewColumn[],
  displayValidationErrorRows: number,
  displayCommentRows: number,
  columnIdToIndex: Map<string, number>,
  searchedRows: Map<string, string[]>,
  filterSearchRows?: boolean,
  columnStore?: CompressedColumnStore | null
) => {
  // OR groups: the row passes the filters when any group matches (a group matches when all of
  // its filters do), or when there are no groups at all.
  let groupCount = 0
  let anyGroupMatched = false

  const rowComments = commentStats ? commentStats.get(row.publicId) : undefined

  for (const key in filterGroups) {
    const group = filterGroups[key]
    groupCount++
    let hasMatch = true

    // If a filter fails to match, we mark the entire group as failing to match because
    // all filters inside a group use the AND clause
    for (const { filter, column } of group) {
      const value = getRowValue(row, filter.columnId, columnIdToIndex, columnStore)
      if (
        !doesFilterMatchValue(
          filter.filterType,
          filter.value,
          filter.multipleValues,
          value,
          userEmail,
          userId,
          userTags,
          processTags,
          processVariables,
          processVariableValues,
          column,
          columnValuesCount,
          row,
          columns,
          columnIdToIndex,
          undefined,
          columnStore
        )
      ) {
        hasMatch = false
        break
      }
    }

    if (hasMatch) anyGroupMatched = true
  }

  // Run comments checks
  let commentsPass = true

  if (displayCommentRows === CommentDisplayTypes.SHOW_RESOLVED) {
    commentsPass =
      rowComments && rowComments.openCommentThreads - rowComments.resolvedCommentThreads === 0 ? true : false
  } else if (displayCommentRows === CommentDisplayTypes.SHOW_UNRESOLVED) {
    commentsPass = rowComments && rowComments.openCommentThreads - rowComments.resolvedCommentThreads > 0 ? true : false
  } else if (displayCommentRows === CommentDisplayTypes.SHOW_WITH_COMMENTS) {
    commentsPass =
      rowComments && (rowComments.openCommentThreads > 0 || rowComments.resolvedCommentThreads > 0) ? true : false
  }

  // Run validation checks
  let validationPass = true

  if (
    displayValidationErrorRows === ValidationDisplayTypes.HIDE_VALIDATION ||
    displayValidationErrorRows === ValidationDisplayTypes.ONLY_VALIDATION
  ) {
    // Convert array-based rowData to object for isValidValue
    const rowDataObj = rowDataToObject(row, columns, columnIdToIndex, columnStore)
    for (const column of columns) {
      const value = getRowValue(row, column.publicId, columnIdToIndex, columnStore)
      const validValue = isValidValue(value, rowDataObj, columnValuesCount, column)

      if (displayValidationErrorRows === ValidationDisplayTypes.HIDE_VALIDATION && validValue.error) {
        validationPass = false
        break
      } else if (displayValidationErrorRows === ValidationDisplayTypes.ONLY_VALIDATION) {
        if (validValue.error) {
          validationPass = true
          break
        } else {
          validationPass = false
        }
      }
    }
  }

  let searchPass = true
  if (filterSearchRows) {
    if (!searchedRows.has(row.publicId)) {
      searchPass = false
    }
  }

  return (groupCount === 0 || anyGroupMatched) && validationPass && commentsPass && searchPass
}

const BOOL_MAP = new Map<string, boolean>([
  ['1', true],
  ['0', false],
  ['true', true],
  ['false', false]
])

function toBoolLike(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1 ? true : v === 0 ? false : null
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return BOOL_MAP.has(s) ? BOOL_MAP.get(s)! : null
  }
  return null
}

// filterMultipleValues are always strings, but a multiselect from a one-to-many lookup of a
// checkbox/number column stores booleans/numbers — stringify the element so they still match.
const elementToFilterString = (element: unknown): string =>
  element !== null && element !== undefined ? (element as { toString(): string }).toString() : ''

export const doesFilterMatchValue = (
  filterType: IFilterType,
  filterValue: string,
  filterMultipleValues: string[],
  value: ICellValue,
  userEmail: string,
  userId: string,
  userTags: ITag[],
  processTags: ITag[],
  processVariables: string[],
  processVariableValues: string[],
  column: ITableViewColumn,
  columnValuesCount: IColumnValuesCount,
  row: ITableRow | null,
  columns?: ITableViewColumn[],
  columnIdToIndex?: Map<string, number>,
  rowData?: { [columnId: string]: ICellValue } | null,
  columnStore?: CompressedColumnStore | null
) => {
  // equal

  if (filterValue === '{{user.email}}' && typeof value === 'string') {
    value = value.toLowerCase()
  }

  const kind = column.kind

  if (kind) {
    if (FilterVariables.includes(filterValue)) {
      filterValue = transformFilterVariable(
        filterValue,
        userEmail,
        userId,
        userTags,
        processTags,
        processVariables,
        processVariableValues,
        kind
      )
    }

    if (filterMultipleValues && filterMultipleValues.some((val) => FilterVariables.includes(val))) {
      const newFilterMultipleValues: string[] = []
      filterMultipleValues.forEach((val) => {
        if (val === '{{user.email_domain}}') {
          newFilterMultipleValues.push(userEmail.split('@')[1])
        } else if (val === '{{user.id}}') {
          newFilterMultipleValues.push(userId)
        } else if (val === '{{user.email}}') {
          newFilterMultipleValues.push(userEmail.toLowerCase())
        } else if (val === '{{user.tags}}') {
          userTags.map((userTag: ITag) => newFilterMultipleValues.push(userTag.referencePublicId))
        } else if (val === '{{user.tag_values}}') {
          userTags.map((userTag: ITag) => newFilterMultipleValues.push(userTag.value))
        } else if (val === '{{process.variables}}') {
          processVariables.map((processVariable: string) => newFilterMultipleValues.push(processVariable))
        } else if (val === '{{process.variable_values}}') {
          processVariableValues.map((processVariableValue: string) =>
            newFilterMultipleValues.push(processVariableValue)
          )
        } else if (val === '{{date.today}}') {
          if (kind === 'datetime') newFilterMultipleValues.push(new Date().toISOString())
          else if (kind === 'date') newFilterMultipleValues.push(new Date().toISOString().split('T')[0])
        } else if (FilterVariables.includes(val)) {
          // A variable with no client-side equivalent (e.g. {{process.tags}}) is passed
          // through untouched so it is not mistaken for a literal date value below.
          newFilterMultipleValues.push(val)
        } else if ((kind === 'datetime' || kind === 'date') && val && val !== null) {
          newFilterMultipleValues.push(val + 'Z')
        } else {
          newFilterMultipleValues.push(val)
        }
      })
      filterMultipleValues = newFilterMultipleValues
    }

    if (filterType === IFilterType.eq) {
      if (kind === 'checkbox') {
        const checkboxValue = value === null ? false : value // keep your current defaulting
        const left = toBoolLike(checkboxValue)
        const right = toBoolLike(filterValue)
        // reject if either side isn't a valid boolean-like
        if (left === null || right === null) return false

        return left === right
      } else {
        if (value == filterValue) {
          return true
        }
      }
    }

    // not equal to
    else if (filterType === IFilterType.neq) {
      if (kind === 'checkbox') {
        const checkboxValue = value === null ? false : value // keep your current defaulting
        const left = toBoolLike(checkboxValue)
        const right = toBoolLike(filterValue)
        // reject if either side isn't a valid boolean-like
        if (left === null || right === null) return false

        return left !== right
      } else {
        if (value != filterValue) {
          return true
        }
      }
    }

    // contains
    else if (
      filterType === IFilterType.contains &&
      value !== null &&
      (typeof value === 'string' || typeof value === 'object') &&
      typeof filterValue === 'string'
    ) {
      if (Array.isArray(value)) {
        if (
          checkArrayValue(value, (v) => typeof v === 'string' && v.toLowerCase().includes(filterValue.toLowerCase()))
        ) {
          return true
        }
      } else if (
        value &&
        !isSummaryUser(value) &&
        filterValue &&
        typeof filterValue === 'string' &&
        value.toLowerCase().includes(filterValue.toLowerCase())
      ) {
        return true
      }
    }

    // does not contain
    else if (
      filterType === IFilterType.not_contains &&
      (value === null || typeof value === 'string' || typeof value === 'object') &&
      typeof filterValue === 'string'
    ) {
      if (value === null) {
        return true
      } else if (Array.isArray(value)) {
        if (
          checkArrayValue(value, (v) => typeof v === 'string' && !v.toLowerCase().includes(filterValue.toLowerCase()))
        ) {
          return true
        }
      } else if (value && !isSummaryUser(value) && !value.toLowerCase().includes(filterValue.toLowerCase())) {
        return true
      }
    }

    // starts with
    else if (filterType === IFilterType.starts_with && typeof value === 'string' && typeof filterValue === 'string') {
      if (value.toLowerCase().startsWith(filterValue.toLowerCase())) {
        return true
      }
    }

    // ends with
    else if (filterType === IFilterType.ends_with && typeof value === 'string' && typeof filterValue === 'string') {
      if (value.toLowerCase().endsWith(filterValue.toLowerCase())) {
        return true
      }
    }

    // is empty
    else if (filterType === IFilterType.is_null) {
      if (Array.isArray(value) && value.length === 0) return true
      else if (value == null || value === '') return true
    }

    // is not empty
    else if (filterType === IFilterType.is_not_null) {
      if (Array.isArray(value) && value.length > 0) return true
      else if (!Array.isArray(value) && value != null) return true
    }

    // is valid
    else if (filterType === IFilterType.is_valid) {
      const rowDataObj =
        row && columns && columnIdToIndex
          ? rowDataToObject(row, columns, columnIdToIndex, columnStore ?? null)
          : (rowData ?? null)
      if (!isValidValue(value, rowDataObj, columnValuesCount, column).error) return true
    }

    // is not valid
    else if (filterType === IFilterType.is_not_valid) {
      const rowDataObj =
        row && columns && columnIdToIndex
          ? rowDataToObject(row, columns, columnIdToIndex, columnStore ?? null)
          : (rowData ?? null)
      if (isValidValue(value, rowDataObj, columnValuesCount, column).error) return true
    }

    // all values: tautological match (used by row-lock and "colour the whole column" rules)
    else if (filterType === IFilterType.all_values) {
      return true
    }

    // is in: filter value contains row value
    else if (filterType === IFilterType.in && typeof value === 'string') {
      if (filterValue !== undefined && filterValue !== null && filterValue.includes(value)) {
        return true
      }
    }

    // one of: row value is in list of filter values
    else if (filterType === IFilterType.one_of && filterMultipleValues && Array.isArray(filterMultipleValues)) {
      if (['multilink', 'multiselect'].includes(kind) && Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          if (filterMultipleValues.indexOf(elementToFilterString(value[i])) !== -1) {
            return true
          }
        }

        return false
      } else {
        if (
          (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) &&
          filterMultipleValues &&
          filterMultipleValues.includes(value !== null && value !== undefined ? value.toString() : '')
        ) {
          return true
        }
      }
    }

    // not one of: row value is in list of filter values
    else if (filterType === IFilterType.not_one_of && filterMultipleValues && Array.isArray(filterMultipleValues)) {
      if (['multilink', 'multiselect'].includes(kind) && Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          if (filterMultipleValues.indexOf(elementToFilterString(value[i])) !== -1) {
            return false
          }
        }

        return true
      } else {
        if (
          (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) &&
          filterMultipleValues &&
          !filterMultipleValues.includes(value !== null && value !== undefined ? value.toString() : '')
        ) {
          return true
        }
      }
    }

    // greater than
    else if (filterType === IFilterType.gt && value !== null) {
      // for date type columns
      if (DATE_COLUMN_TYPES.includes(kind)) {
        const dateValue = new Date(value as string)
        const filterDateValue = new Date(filterValue)
        return dateValue > filterDateValue
      }
      // anything else we treat as a number
      else {
        return Number(value) > parseFloat(filterValue)
      }
    }

    // less than
    else if (filterType === IFilterType.lt && value !== null) {
      if (DATE_COLUMN_TYPES.includes(kind)) {
        const dateValue = new Date(value as string)
        const filterDateValue = new Date(filterValue)
        return dateValue < filterDateValue
      } else {
        return Number(value) < parseFloat(filterValue)
      }
    }

    // greater than or equal to
    else if (filterType === IFilterType.gte && value !== null) {
      if (DATE_COLUMN_TYPES.includes(kind)) {
        const dateValue = new Date(value as string)
        const filterDateValue = new Date(filterValue)
        return dateValue >= filterDateValue
      } else {
        return Number(value) >= parseFloat(filterValue)
      }
    }

    // less than or equal to
    else if (filterType === IFilterType.lte && value !== null) {
      if (DATE_COLUMN_TYPES.includes(kind)) {
        const dateValue = new Date(value as string)
        const filterDateValue = new Date(filterValue)
        return dateValue <= filterDateValue
      } else {
        return Number(value) <= parseFloat(filterValue)
      }
    }
  }

  return false
}

const transformFilterVariable = (
  value: string,
  userEmail: string,
  userId: string,
  userTags: ITag[],
  processTags: ITag[],
  processVariables: string[],
  processVariableValues: string[],
  kind: IColumnTypes
) => {
  let transformedValue = value
  if (value === '{{user.email_domain}}') transformedValue = userEmail.split('@')[1]
  else if (value === '{{user.email}}') transformedValue = userEmail.toLowerCase()
  else if (value === '{{user.id}}') transformedValue = userId
  else if (value === '{{user.tags}}')
    transformedValue = userTags.map((userTag: ITag) => userTag.referencePublicId).join(',')
  else if (value === '{{user.tag_values}}') transformedValue = userTags.map((userTag: ITag) => userTag.value).join(',')
  else if (value === '{{process.tags}}')
    transformedValue = processTags.map((processTag: ITag) => processTag.referencePublicId).join(',')
  else if (value === '{{process.variables}}') transformedValue = processVariables.join(',')
  else if (value === '{{process.variable_values}}') transformedValue = processVariableValues.join(',')
  else if (value === '{{date.today}}') {
    if (kind === 'datetime') transformedValue = new Date().toISOString()
    else if (kind === 'date') transformedValue = new Date().toISOString().split('T')[0]
  } else transformedValue = value

  return transformedValue
}

const checkArrayValue = (value: Array<string | IFile | IVote | null>, predicate: (value: string) => boolean) => {
  // Get the string values from IFile if the value is of that type
  const arrayStringValues =
    value === null
      ? []
      : value
          .filter((v) => v !== null)
          .map((v: string | IFile | IVote | null) => ((v as IFile).filename ? (v as IFile).filename : v) as string)
          .filter((v) => v !== null)

  // check if any of the string includes or not the filter value
  return arrayStringValues.map(predicate).some((v) => v === true)
}

export const isValidFilterTypeForColType = (colType: IColumnTypes, filterType: IFilterType): boolean => {
  const stringTypes = [...TEXT_COLUMN_TYPES, 'select', 'link']
  const numberTypes = NUMBER_COLUMN_TYPES
  const simpleArrayTypes = ['multiselect', 'multilink']
  // these are arrays of objects, rather than arrays of numbers/strings
  const complexArrayTypes = [...ATTACHMENT_COLUMN_TYPES, 'vote']
  const arrayTypes = [...simpleArrayTypes, ...complexArrayTypes]
  const booleanTypes = BOOLEAN_COLUMN_TYPES

  // JSON columns only support is_null / is_not_null (per design — values are
  // arbitrary objects, so contains/eq/etc are not meaningful).
  if (colType === 'json') {
    return filterType === IFilterType.is_null || filterType === IFilterType.is_not_null
  }

  // supported by all remaining types (json handled above)
  if (filterType === IFilterType.is_valid || filterType === IFilterType.is_not_valid) {
    return true
  }

  // tautological match — supported by every remaining column type (json handled above)
  if (filterType === IFilterType.all_values) {
    return true
  }

  // supported by all remaining types apart from boolean (json handled above)
  if (filterType === IFilterType.is_null || filterType === IFilterType.is_not_null) {
    return !booleanTypes.includes(colType)
  }

  // supported by all types except array types
  if (filterType === IFilterType.eq || filterType === IFilterType.neq) {
    return !arrayTypes.includes(colType)
  }

  // supported only by numbers, strings, and non-complex array types
  if (filterType === IFilterType.one_of || filterType === IFilterType.not_one_of) {
    return !complexArrayTypes.includes(colType) && !booleanTypes.includes(colType)
  }

  // only supported by string and array types
  if (filterType === IFilterType.contains || filterType === IFilterType.not_contains) {
    return stringTypes.includes(colType) || arrayTypes.includes(colType)
  }

  // only supported by string types
  if (filterType === IFilterType.starts_with || filterType === IFilterType.ends_with || filterType === IFilterType.in) {
    return stringTypes.includes(colType)
  }

  // only supported by number fields and date fields
  if (
    filterType == IFilterType.gt ||
    filterType == IFilterType.gte ||
    filterType == IFilterType.lt ||
    filterType == IFilterType.lte
  ) {
    return numberTypes.includes(colType) || DATE_COLUMN_TYPES.includes(colType)
  }

  return false
}

export const getDefaultFilterTypeForColType = (colType: IColumnTypes): IFilterType => {
  if (BOOLEAN_COLUMN_TYPES.includes(colType)) {
    return IFilterType.eq
  } else if (ATTACHMENT_COLUMN_TYPES.includes(colType) || VOTE_COLUMN_TYPES.includes(colType)) {
    return IFilterType.is_not_null
  } else if (colType === 'json') {
    return IFilterType.is_not_null
  }

  return IFilterType.one_of
}

export const getFilterValues = (
  rows: ITableRow[],
  column: ITableViewColumn,
  columnId: string,
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  const values = new Set()
  if (column) {
    const shouldFlatten = ['multilink', 'multiselect'].includes(column.kind)
    for (const row of rows) {
      const cellValue = getRowValue(row, columnId, columnIdToIndex, columnStore)
      if (shouldFlatten && cellValue && Array.isArray(cellValue)) {
        cellValue.forEach((value) => values.add(value))
      } else {
        values.add(cellValue)
      }
    }
  }

  return values
}
